import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useHistory } from "react-router-dom";
import * as XLSX from "xlsx";

import {
  ListProductsActions,
  ListProductsState,
  PaginateProductsActions,
  PaginateProductsState,
} from "store/ducks/settings/products";

import { SelectedFilterActions, SelectedFilterState } from "store/ducks/filter";

import * as S from "./styles";
import { useTranslation, usePermissions } from "hooks";
import { translations } from "./translations";

import { MainContainer, Paginator, MenuSelector } from "components/shared";
import { GridProducts, FilterProducts } from "components/settings";

export const ListProducts: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);
  const { checkUserPermission } = usePermissions();

  const [query, setQuery] = useState({
    limit: 10,
    page: 1,
  });

  const { data, loading, pagination } = useSelector<RootStateOrAny>(
    (state) => state.paginateProducts
  ) as PaginateProductsState;

  const { loading: loadingExport } = useSelector<RootStateOrAny>(
    (state) => state.listProducts
  ) as ListProductsState;

  const { data: dataFilter } = useSelector<RootStateOrAny>(
    (state) => state.selectedFilter
  ) as SelectedFilterState;

  const exportarParaXLSX = (dados: any) => {
    if (dados.length > 0) {
      const nameFile = "RELATÓRIO-PRODUTOS.xlsx";
      const file = XLSX.utils.book_new();
      const wsData = dados.map((item: any) => [
        item.id,
        item.code,
        item.attribuition || "--",
        item.description,
        item.business_line?.activity_division || "--",
        item.product_line?.name || "--",
        item.plant?.name || "--",
        item?.pallet_quantity || "--",
        item?.pallet_unity || "--",
        item.product_type?.description || "--",
        item.pallet_type?.description || "--",
        item.risk?.name || "--",
        item.risk_number || "--",
        item.uno_sap || "--",
        item.subsidiary_risk || "--",
        item?.packing_group || "--",
        item?.frb || "--",
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([
        [
          "id",
          "codigo",
          "atribuição",
          "descrição",
          "linha negocio",
          "linha produto",
          "plantas",
          "quantidade pallet",
          "unidade pallet",
          "tipo produto",
          "tipo pallet",
          "risco produto",
          "numero risco",
          "onu sap",
          "risco subsidiario",
          "embalagem grupo",
          "frb",
        ],
        ...wsData,
      ]);
      XLSX.utils.book_append_sheet(file, worksheet, "Dados");
      XLSX.writeFile(file, nameFile);
    } else {
      alert("Não há dados para exportar");
    }
  };

  const getListProducts = useCallback(() => {
    dispatch(
      ListProductsActions.request(
        { all: true, ...dataFilter },
        (dados: any) => {
          exportarParaXLSX(dados);
        }
      )
    );
  }, [dispatch, dataFilter]);

  const handleSubmitFilter = useCallback(
    (value) => {
      const data = {
        ...dataFilter,
        ...value,
        page: 1,
      };
      dispatch(SelectedFilterActions.success(data));
      setQuery((state) => ({ ...state, data }));
    },
    [setQuery, dispatch, dataFilter]
  );

  const handlePageChange = useCallback(
    (value) => {
      const data = {
        ...dataFilter,
        page: value,
      };
      dispatch(SelectedFilterActions.success(data));
      setQuery((state) => ({ ...state, data }));
    },
    [setQuery, dispatch, dataFilter]
  );

  const getData = useCallback(() => {
    dispatch(PaginateProductsActions.request({ ...query, ...dataFilter }));
  }, [dispatch, query, dataFilter]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <MainContainer>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation("configuracoes")} <MenuSelector page="products" />
          {loading && <S.LoadingPage />}
        </h1>
        <S.HeaderButtons>
          {checkUserPermission("insertProduct") && (
            <S.ButtonMini onClick={getListProducts}>
              {loadingExport ? <S.LoadingPage /> : getTranslation("exportar")}
            </S.ButtonMini>
          )}
          {checkUserPermission("insertProduct") && (
            <S.ButtonMini
              onClick={() => history.push("/settings/products/new")}
            >
              {getTranslation("novoProduto")}
            </S.ButtonMini>
          )}
          <FilterProducts onFilter={handleSubmitFilter} />
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <GridProducts products={data} />
        <Paginator
          pagination={pagination}
          onPageChange={handlePageChange}
          loading={loading}
        />
      </S.PageContent>
    </MainContainer>
  );
};
